import { useId } from 'react';
import { EChart as ReactEChartsCore } from './EChart';
import * as echarts from 'echarts/core';
import { GaugeChart as EChartsGauge } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';

import { PHASE_BUDGET } from '../../data/fixtures';
import { formatMoney, formatPercentage } from '../../i18n/format';
import { ChartFrame } from './ChartFrame';
import { seriesColor, useChartMotion, useChartTheme } from './ChartTheme';

// A gauge needs no grid, tooltip or aria component — it is a single value.
echarts.use([EChartsGauge, CanvasRenderer]);

const money = (value: number) => formatMoney(value, { precision: 0 });

export function RadialGauge() {
  const theme = useChartTheme();
  const motion = useChartMotion(theme);
  const summaryId = useId();

  const budgeted = PHASE_BUDGET.reduce((total, row) => total + row.budgeted, 0);
  const actual = PHASE_BUDGET.reduce((total, row) => total + row.actual, 0);
  const consumed = budgeted > 0 ? (actual / budgeted) * 100 : 0;
  const remaining = budgeted - actual;

  const percentage = formatPercentage(consumed, { precision: 1 });

  return (
    <ChartFrame
      title="Budget consumed"
      description="Committed spend against the total funded budget."
      summary={`Budget consumed: ${percentage} of ${money(budgeted)}. ${money(actual)} spent, ${money(
        remaining,
      )} remaining.`}
      // The ring is not an image: it carries progress semantics itself, so the
      // frame does not wrap it in role="img".
      chartRole="none"
      summaryId={summaryId}
      height={280}
      table={{
        caption: 'Budget consumed against the total funded budget',
        columns: ['Measure', 'Value'],
        rows: [
          ['Budget consumed', percentage],
          ['Spent', money(actual)],
          ['Budget', money(budgeted)],
          ['Remaining', money(remaining)],
        ],
      }}
    >
      <div
        role="progressbar"
        aria-label="Budget consumed"
        aria-valuenow={Number(consumed.toFixed(1))}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={percentage}
        aria-describedby={summaryId}
        className="relative size-full"
      >
        <ReactEChartsCore
          echarts={echarts}
          style={{ width: '100%', height: '100%' }}
          option={{
            animation: motion.active,
            animationDuration: motion.duration,
            animationDurationUpdate: motion.duration,
            animationEasing: motion.easing,
            series: [
              {
                id: 'budget-consumed',
                type: 'gauge',
                min: 0,
                max: 100,
                startAngle: 220,
                endAngle: -40,
                radius: '92%',
                center: ['50%', '58%'],
                // Square cap: the Constructivist language has no rounded ends.
                progress: {
                  show: true,
                  width: 16,
                  roundCap: false,
                  itemStyle: { color: seriesColor(theme, 0) },
                },
                axisLine: { lineStyle: { width: 16, color: [[1, theme.grid]] } },
                pointer: { show: false },
                anchor: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                axisLabel: { show: false },
                detail: { show: false },
                data: [{ value: consumed }],
              },
            ],
          }}
        />
        {/* Visible value label. The value is also in aria-valuetext above, so a
            screen reader reads it from the progressbar rather than from here. */}
        {/* No `tabular-nums`: this is `font-display` (Oswald), which ships no
            `tnum` feature — see the note beside `--font-display` in tokens.css
            and `Metric.tsx`. The declaration was inert here. */}
        <p className="pointer-events-none absolute inset-0 grid place-items-center pb-6 text-center font-display text-3xl font-semibold text-heading">
          {percentage}
        </p>
      </div>
    </ChartFrame>
  );
}
